'use client';

import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import productsData from '@/data/products.json';

export interface CartItem {
  productId: string;
  name: string;
  size: string;
  price: number;
  priceEur: number;
  quantity: number;
  image: string;
  stripePriceId: string;
  stripePriceIdEur: string;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void;
  removeItem: (productId: string, size: string) => void;
  updateQuantity: (productId: string, size: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
  isCartOpen: boolean;
  setCartOpen: (open: boolean) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_KEY = 'klonkaffe-cart';

function migrateCartItem(item: Partial<CartItem>): CartItem | null {
  if (item.priceEur && item.stripePriceIdEur) return item as CartItem;

  const product = (productsData as Array<{ id: string; variants: Array<{ size: string; price: number; priceEur: number; stripePriceId: string; stripePriceIdEur: string; stripeRecurringPriceId: string; stripeRecurringPriceIdEur: string }> }>)
    .find((p) => p.id === item.productId);
  if (!product) return null;

  const variant = product.variants.find((v) => v.size === item.size);
  if (!variant) return null;

  return {
    ...item,
    price: variant.price,
    priceEur: variant.priceEur,
    stripePriceId: variant.stripePriceId,
    stripePriceIdEur: variant.stripePriceIdEur,
  } as CartItem;
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isCartOpen, setCartOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const stored = localStorage.getItem(CART_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as Partial<CartItem>[];
        const migrated = parsed.map(migrateCartItem).filter(Boolean) as CartItem[];
        setItems(migrated);
      } catch {
        localStorage.removeItem(CART_KEY);
      }
    }
  }, []);

  useEffect(() => {
    if (mounted) {
      localStorage.setItem(CART_KEY, JSON.stringify(items));
    }
  }, [items, mounted]);

  const addItem = useCallback((newItem: Omit<CartItem, 'quantity'>, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find(
        (i) => i.productId === newItem.productId && i.size === newItem.size
      );
      if (existing) {
        return prev.map((i) =>
          i.productId === newItem.productId && i.size === newItem.size
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }
      return [...prev, { ...newItem, quantity }];
    });
    setCartOpen(true);
  }, []);

  const removeItem = useCallback((productId: string, size: string) => {
    setItems((prev) =>
      prev.filter((i) => !(i.productId === productId && i.size === size))
    );
  }, []);

  const updateQuantity = useCallback(
    (productId: string, size: string, quantity: number) => {
      if (quantity <= 0) {
        removeItem(productId, size);
        return;
      }
      setItems((prev) =>
        prev.map((i) =>
          i.productId === productId && i.size === size
            ? { ...i, quantity }
            : i
        )
      );
    },
    [removeItem]
  );

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        totalItems,
        totalPrice,
        isCartOpen,
        setCartOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
}
